import { TimeEntry, WorkdaysPerWeek } from "../store/timeTrackerStore";

export interface DateRange {
  start: Date;
  end: Date;
}

const HOUR_MS = 60 * 60 * 1000;

const startOfDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

// Monday-anchored week start, matching the rest of the app.
const startOfWeek = (d: Date): Date => {
  const out = startOfDay(d);
  const day = out.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  return out;
};

const startOfMonth = (d: Date): Date => {
  const out = startOfDay(d);
  out.setDate(1);
  return out;
};

// [Monday 00:00 of this calendar week, now].
export function getCurrentWeekRange(now: Date = new Date()): DateRange {
  return { start: startOfWeek(now), end: now };
}

// [00:00 of today, now].
export function getCurrentDayRange(now: Date = new Date()): DateRange {
  return { start: startOfDay(now), end: now };
}

// Calendar week N weeks before this one as a complete [Mon, next Mon) range.
// weeksBack=1 → last completed week. weeksBack=0 → this week as a full Mon→Mon range.
export function getCompletedWeekRange(
  now: Date,
  weeksBack: number,
): DateRange {
  const thisMonday = startOfWeek(now);
  const start = new Date(thisMonday);
  start.setDate(start.getDate() - weeksBack * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { start, end };
}

// [1st 00:00 of current calendar month, now].
export function getCurrentMonthRange(now: Date = new Date()): DateRange {
  return { start: startOfMonth(now), end: now };
}

// Where an even pace across `workdaysPerWeek` Monday-anchored days would have
// you by end of today, as a fraction of the weekly target. Discrete by day so
// the tick reads as a daily goal: with 4 workdays, Mon → 1/4 … Thu → 4/4;
// with 5, Mon → 1/5 … Fri → 5/5; with 6, Mon → 1/6 … Sat → 6/6; with 7,
// every day advances by 1/7. Off-days (anything past the last workday) cap
// at 1 so off-day hours read as catch-up rather than as falling behind.
export function getWeekPaceFraction(
  now: Date = new Date(),
  workdaysPerWeek: WorkdaysPerWeek = 5,
): number {
  const dow = now.getDay(); // 0=Sun, 1=Mon, … 6=Sat
  // Treat Sun as day 7 so it falls after Sat in a Mon-anchored ordering.
  const dayIndex = dow === 0 ? 7 : dow;
  if (dayIndex > workdaysPerWeek) return 1;
  return dayIndex / workdaysPerWeek;
}

// Sum hours per client id for completed entries whose startTime falls in
// [startMs, endMs). Entries without a clientId roll into "unassigned".
// Running entries (no endTime) are skipped — they aren't billable yet.
export function sumHoursByClient(
  entries: TimeEntry[],
  startMs: number,
  endMs: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entry of entries) {
    if (entry.endTime === undefined) continue;
    if (entry.startTime < startMs || entry.startTime >= endMs) continue;
    const id = entry.clientId || "unassigned";
    const hours = (entry.endTime - entry.startTime) / HOUR_MS;
    out[id] = (out[id] || 0) + hours;
  }
  return out;
}

const SPARKLINE_CHARS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

// Render a Unicode block-char sparkline from a numeric series. Scale is shared
// across the series so adjacent values are comparable; an all-zero series
// renders as the lowest tick repeated.
export function sparklineFromValues(values: number[]): string {
  if (values.length === 0) return "";
  const max = Math.max(...values);
  if (max <= 0) return SPARKLINE_CHARS[0].repeat(values.length);
  const lastIdx = SPARKLINE_CHARS.length - 1;
  return values
    .map((v) => {
      const idx = Math.min(
        lastIdx,
        Math.max(0, Math.round((v / max) * lastIdx)),
      );
      return SPARKLINE_CHARS[idx];
    })
    .join("");
}
